/* PDP sock upsell (design 2a) — a checkbox row that RIDES WITH Add to cart.

   When the row is ticked, the selected sock is added to the cart the moment the shoe's own
   Add to cart succeeds. We listen for the theme's `ajaxProduct:added` document event, which the
   product form dispatches ONLY on a successful add (never on the error path) — so socks can never
   land in the bag without the shoe, and the form's own logic (quantity, selling plan, the
   Women's-size line-item property) is left completely untouched.

   Colourways are separate products; each swatch carries its own variant id, price, compare-at,
   image and colour name. Selecting one swaps the thumbnail + colour name and sets which variant
   rides along. The add itself mirrors the theme's own: request the drawer's sections, publish
   cartUpdate so the drawer + count re-render, then open the drawer.

   TWO MODES, chosen from whether this shoe is already in the cart (checked on load and on every
   cartUpdate): if it ISN'T, the row is the ride-along checkbox above. If it IS, ticking could
   never ride along with an add, so the row becomes a direct "add to bag" button (bag icon) — a
   click adds the selected sock straight to the cart. */
if (!customElements.get('sock-upsell')) {
  customElements.define(
    'sock-upsell',
    class SockUpsell extends HTMLElement {
      connectedCallback() {
        this.check = this.querySelector('[data-sock-check]');
        this.priceEl = this.querySelector('[data-sock-price]');
        this.compareEl = this.querySelector('[data-sock-compare]');
        this.imgEl = this.querySelector('[data-sock-img]');
        this.nameEl = this.querySelector('[data-sock-name]');
        this.linkEl = this.querySelector('[data-sock-link]');
        this.label = this.querySelector('[data-sock-add]');
        this.productId = String(this.dataset.productId || '');
        this.swatches = Array.from(this.querySelectorAll('[data-sock-swatch]'));
        this.single = this.querySelector('[data-sock-single]');
        if (!this.check) return;

        this.swatches.forEach((btn) =>
          btn.addEventListener('click', () => this.selectSwatch(btn))
        );

        // Ride-along (checkbox mode): add the sock the instant the shoe's own add-to-cart succeeds.
        this.onAdded = this.onAdded.bind(this);
        document.addEventListener('ajaxProduct:added', this.onAdded);

        // Button mode: when the shoe is ALREADY in the cart, ticking can't ride along with an add,
        // so a click on the row adds the sock straight to the bag instead.
        if (this.label) {
          this.label.addEventListener('click', (e) => {
            if (e.target.closest('[data-sock-link]')) return; // the product name link navigates
            if (this.inCart) { e.preventDefault(); this.addSock(); }
          });
        }
        // The theme add button shown in button mode (matches the cart quick-add).
        this.addBtn = this.querySelector('[data-sock-addbtn]');
        if (this.addBtn) this.addBtn.addEventListener('click', () => this.addSock());
        // Keyboard: space on the (hidden) checkbox fires change — treat it as an add in button mode.
        this.check.addEventListener('change', () => {
          if (this.inCart && this.check.checked) { this.check.checked = false; this.addSock(); }
        });

        // Pick the mode now, and re-pick it whenever the cart changes on this page.
        this.onCartUpdate = this.refreshMode.bind(this);
        this.refreshMode();
        this.subscribeCart();
      }

      // theme.pubsub may not exist yet when this element connects — retry until it does.
      subscribeCart(tries) {
        tries = tries || 0;
        if (typeof theme !== 'undefined' && theme.pubsub && theme.pubsub.subscribe) {
          this.cartUnsub = theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, this.onCartUpdate);
        } else if (tries < 20) {
          setTimeout(() => this.subscribeCart(tries + 1), 250);
        }
      }

      disconnectedCallback() {
        document.removeEventListener('ajaxProduct:added', this.onAdded);
        if (this.cartUnsub) this.cartUnsub();
      }

      // Ride-along vs direct-add depends on whether this shoe is already in the cart.
      async refreshMode() {
        this.inCart = await this.shoeInCart();
        this.classList.toggle('is-incart', this.inCart);
      }

      async shoeInCart() {
        if (!this.productId) return false;
        try {
          // Fetch /cart with Accept: json (NOT /cart.js — a store app breaks that endpoint), and
          // don't depend on the `theme` global, which may not exist yet when this element connects
          // on page load — that was why button mode never activated.
          const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
          const cart = await (await fetch(root + 'cart', { headers: { Accept: 'application/json' } })).json();
          return (cart.items || []).some((i) => String(i.product_id) === this.productId);
        } catch (e) {
          return false;
        }
      }

      get variantId() {
        const pressed = this.swatches.find((b) => b.getAttribute('aria-pressed') === 'true');
        if (pressed) return pressed.dataset.variant;
        return this.single ? this.single.value : null;
      }

      selectSwatch(btn) {
        this.swatches.forEach((b) => b.setAttribute('aria-pressed', b === btn ? 'true' : 'false'));
        if (this.imgEl && btn.dataset.img) this.imgEl.src = btn.dataset.img;
        if (this.priceEl) this.priceEl.textContent = btn.dataset.price || '';
        if (this.compareEl) {
          const c = btn.dataset.compare || '';
          this.compareEl.textContent = c;
          this.compareEl.hidden = !c;
        }
        if (this.nameEl && btn.dataset.title) this.nameEl.textContent = btn.dataset.title;
        if (this.linkEl && btn.dataset.url) this.linkEl.href = btn.dataset.url;
      }

      onAdded() {
        // Ride-along: add the sock if the box is ticked (checkbox mode).
        if (this.check.checked) this.addSock();
        // The shoe was just added — re-check so the row flips to the add-to-bag button.
        this.refreshMode();
      }

      addSock() {
        if (this.busy) return;
        const variantId = this.variantId;
        if (!variantId) return;
        this.busy = true;

        const sections = [];
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:bundled-sections', { bubbles: true, detail: { sections } })
        );

        const config = theme.utils.fetchConfig('javascript');
        config.headers['Content-Type'] = 'application/json';
        config.body = JSON.stringify({
          items: [{ id: variantId, quantity: 1 }],
          sections,
          sections_url: window.location.pathname,
        });

        fetch(theme.routes.cart_add_url, config)
          .then((response) => response.json())
          .then(async (parsed) => {
            if (parsed.status) {
              console.error('[sock-upsell]', parsed.description);
              return;
            }
            // Reset the opt-in as soon as the socks are confirmed in the bag — before the cart
            // re-fetch / drawer work below, so a throw there can't leave the box stuck ticked.
            // The tick is "add socks with this add-to-cart", not a running "socks are in your
            // cart" state, so it must not persist after the socks land (or are later removed).
            this.check.checked = false;
            const cart = await (
              await fetch(theme.routes.cart_url, { ...theme.utils.fetchConfig('json', 'GET') })
            ).json();
            cart.sections = parsed.sections;
            theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
              source: 'sock-upsell',
              productVariantId: variantId,
              cart,
            });
            document.querySelector('cart-drawer')?.show();
          })
          .catch((error) => console.error('[sock-upsell]', error))
          .finally(() => {
            this.busy = false;
          });
      }
    }
  );
}
